/**
 * tdesign v1.1.1
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-8c8a3203.js';
import { _ as _slicedToArray } from '../_chunks/dep-605e961c.js';
import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { canUseDocument, addClass, removeClass } from '../_util/dom.js';
import useConfig from '../hooks/useConfig.js';
import Portal from '../common/Portal.js';
import GradientLoading from './gradient.js';
import { loadingDefaultProps } from './defaultProps.js';
import '../_chunks/dep-f66447d3.js';
import 'raf';
import '../_chunks/dep-378695bd.js';
import '../_chunks/dep-8670d51c.js';
import '../_chunks/dep-c7d784af.js';
import '../_chunks/dep-2055661d.js';
import '../_chunks/dep-868937ef.js';
import '../_chunks/dep-dae4a585.js';
import '../_util/easing.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-1d5f47c2.js';
import '../_chunks/dep-df9f8f04.js';
import '../_chunks/dep-9d326786.js';
import '../_chunks/dep-3542b5f1.js';
import '../_chunks/dep-79aca757.js';
import '../_chunks/dep-73c3cd54.js';
import '../_chunks/dep-ced835e2.js';
import '../_chunks/dep-1e76a561.js';
import '../_chunks/dep-6b33e1e9.js';
import '../_chunks/dep-9e05eaf2.js';
import '../_chunks/dep-0d617aaa.js';
import '../_chunks/dep-030d278c.js';
import '../_chunks/dep-6f5c07a7.js';
import '../_chunks/dep-a79a1775.js';
import '../_chunks/dep-7b744c82.js';
import '../_chunks/dep-0b20126b.js';
import '../_chunks/dep-2b7035c1.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-4fce6f2f.js';
import '../_common/js/global-config/default-config.js';
import 'react-dom';
import '../hooks/useDomRefCallback.js';
import '../_common/js/loading/circle-adapter.js';
import '../_common/js/utils/set-style.js';
import '../_common/js/utils/helper.js';
import '../_chunks/dep-f41383e1.js';
import '../_chunks/dep-97bb7f6b.js';
import '../_chunks/dep-01305891.js';
import '../_chunks/dep-bd50d435.js';
import '../_chunks/dep-18226b92.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var Loading = function Loading(props) {
  var attach = props.attach,
    indicator = props.indicator,
    text = props.text,
    loading = props.loading,
    size = props.size,
    delay = props.delay,
    fullscreen = props.fullscreen,
    preventScrollThrough = props.preventScrollThrough,
    showOverlay = props.showOverlay,
    content = props.content,
    children = props.children,
    inheritColor = props.inheritColor,
    zIndex = props.zIndex,
    className = props.className,
    style = props.style;
  var _useState = useState(delay ? false : loading),
    _useState2 = _slicedToArray(_useState, 2),
    showLoading = _useState2[0],
    setShowLoading = _useState2[1];
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var name = "".concat(classPrefix, "-loading");
  var centerClass = "".concat(classPrefix, "-loading--center");
  var inheritColorClass = "".concat(classPrefix, "-loading--inherit-color");
  var fullClass = "".concat(classPrefix, "-loading--full");
  var fullscreenClass = "".concat(classPrefix, "-loading__fullscreen");
  var lockClass = "".concat(classPrefix, "-loading--lock");
  var overlayClass = "".concat(classPrefix, "-loading__overlay");
  var relativeClass = "".concat(classPrefix, "-loading__parent");
  var textClass = "".concat(classPrefix, "-loading__text");
  useEffect(function () {
    var timer;
    if (delay && loading) {
      timer = setTimeout(function () {
        setShowLoading(loading);
      }, delay);
    } else {
      setShowLoading(loading);
    }
    return function () {
      clearTimeout(timer);
    };
  }, [delay, loading]);
  var calcStyles = useMemo(function () {
    var styles = {};
    if (zIndex !== void 0) {
      styles.zIndex = zIndex;
    }
    if (!["small", "medium", "large"].includes(size)) {
      styles.fontSize = size;
    }
    return style ? _objectSpread(_objectSpread({}, styles), style) : styles;
  }, [size, zIndex, style]);
  var sizeMap = {
    large: "".concat(classPrefix, "-size-l"),
    small: "".concat(classPrefix, "-size-s"),
    medium: "".concat(classPrefix, "-size-m")
  };
  var baseClasses = classNames(centerClass, sizeMap[size], _defineProperty({}, inheritColorClass, inheritColor), className);
  useEffect(function () {
    if (preventScrollThrough && fullscreen && canUseDocument && loading) {
      addClass(document.body, lockClass);
    }
    return function () {
      removeClass(document.body, lockClass);
    };
  }, [loading, preventScrollThrough, fullscreen, lockClass]);
  var commonContent = function commonContent() {
    var renderIndicator = /* @__PURE__ */React.createElement(GradientLoading, null);
    if (indicator && typeof indicator !== "boolean") {
      renderIndicator = indicator;
    }
    return /* @__PURE__ */React.createElement(React.Fragment, null, indicator ? renderIndicator : null, text ? /* @__PURE__ */React.createElement("div", {
      className: textClass
    }, text) : null);
  };
  if (fullscreen) {
    return loading ? /* @__PURE__ */React.createElement("div", {
      className: classNames(name, fullscreenClass, centerClass, overlayClass),
      style: calcStyles
    }, /* @__PURE__ */React.createElement("div", {
      className: baseClasses
    }, commonContent())) : null;
  }
  if (content || children) {
    return /* @__PURE__ */React.createElement("div", {
      className: relativeClass
    }, content || children, showLoading ? /* @__PURE__ */React.createElement("div", {
      className: classNames(name, baseClasses, fullClass, _defineProperty({}, overlayClass, showOverlay)),
      style: calcStyles
    }, commonContent()) : null);
  }
  if (attach) {
    return /* @__PURE__ */React.createElement(Portal, {
      attach: attach
    }, loading ? /* @__PURE__ */React.createElement("div", {
      className: classNames(name, baseClasses, fullClass, _defineProperty({}, overlayClass, showOverlay)),
      style: calcStyles
    }, commonContent()) : null);
  }
  return loading ? /* @__PURE__ */React.createElement("div", {
    className: classNames(name, baseClasses),
    style: calcStyles
  }, commonContent()) : null;
};
Loading.displayName = "Loading";
Loading.defaultProps = loadingDefaultProps;

export { Loading as default };
//# sourceMappingURL=Loading.js.map
